import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { SYNC_FIELDS_LABELS, EVENTI, MOMENTI_ANNULLO, RESO_VALORI, STATI_CONFERMA, STATI_CONSEGNA, STATI_ORDINE } from "../lib/constants";

const AZIONI = [
  { id: "mantieni", label: "Mantieni manuale", cls: "bg-slate-100 text-slate-700 hover:bg-slate-200" },
  { id: "automatica", label: "Accetta esterno e torna automatico", cls: "bg-emerald-600 text-white hover:bg-emerald-700" },
];

function SimulaSync({ stores, onDone }) {
  const [f, setF] = useState({ store_id: stores[0]?.id || "", id_ordine_esterno: "", campo: "stato_consegna", valore: "" });
  const [ris, setRis] = useState(null);
  const esegui = async () => {
    try {
      const res = await api.post("/sync/simula", {
        ordini: [{ store_id: f.store_id, id_ordine_esterno: f.id_ordine_esterno, campo: f.campo, valore: f.valore }],
        adv: [],
      });
      setRis(res.data);
      toast.success("Sincronizzazione simulata");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const inp = "border border-slate-300 rounded-md px-2 py-1.5 text-sm";
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
      <h2 className="text-sm font-semibold text-slate-800 mb-1">Simula sincronizzazione esterna (dati finti)</h2>
      <p className="text-xs text-slate-400 mb-3">In FASE 1 non ci sono integrazioni attive: questo pannello simula l'arrivo di un valore da Shopify / Spediamo Pro per verificare la regola "il dato corretto a mano è il dato ufficiale".</p>
      <div className="flex flex-wrap gap-2">
        <select data-testid="sync-store" className={inp} value={f.store_id} onChange={(e) => setF({ ...f, store_id: e.target.value })}>
          {stores.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
        </select>
        <input data-testid="sync-ordine" className={inp} placeholder="N° ordine" value={f.id_ordine_esterno} onChange={(e) => setF({ ...f, id_ordine_esterno: e.target.value })} />
        <select data-testid="sync-campo" className={inp} value={f.campo} onChange={(e) => setF({ ...f, campo: e.target.value })}>
          {Object.entries(SYNC_FIELDS_LABELS).filter(([k]) => k !== "importo").map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input data-testid="sync-valore" className={inp} placeholder="Valore esterno" value={f.valore} onChange={(e) => setF({ ...f, valore: e.target.value })} />
        <button data-testid="sync-esegui" onClick={esegui} disabled={!f.id_ordine_esterno}
          className="bg-slate-900 text-white text-sm font-semibold rounded-md px-4 py-1.5 disabled:opacity-40">Simula</button>
      </div>
      {ris && (
        <div data-testid="sync-risultato" className="mt-2 text-xs font-mono text-slate-600">
          Applicati: {ris.applicati} · Coincidenti: {ris.coincidenti} · Divergenze generate: {ris.divergenze}
          {ris.errori.length > 0 && <span className="text-red-600"> · Errori: {ris.errori.join(", ")}</span>}
        </div>
      )}
    </div>
  );
}

export default function DivergenzePage() {
  const { stores, storeId, isMaster } = useOutletContext();
  const [divs, setDivs] = useState(null);
  const [sel, setSel] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = () => {
    api.get(`/divergenze?store_id=${storeId}&stato=aperta`)
      .then((r) => { setDivs(r.data); setSel({}); })
      .catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { setDivs(null); load(); }, [storeId]);

  const risolvi = async (d, azione) => {
    try {
      await api.post(`/divergenze/${d.id}/risolvi`, { azione });
      toast.success(`Divergenza risolta: ${azione}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const selezionate = (divs || []).filter((d) => sel[d.id]);
  const tutteSel = (divs || []).length > 0 && selezionate.length === divs.length;
  const bulkAutomatico = async () => {
    setBulkBusy(true);
    let ok = 0, ko = 0;
    for (const d of selezionate) {
      // ogni risoluzione passa dall'endpoint singolo: valore esterno applicato,
      // campo rimesso in automatica e voce nel log modifiche per ciascuna
      try { await api.post(`/divergenze/${d.id}/risolvi`, { azione: "automatica" }); ok++; }
      catch { ko++; }
    }
    setBulkBusy(false);
    if (ko) toast.error(`${ko} divergenze non risolte (vedi log)`);
    if (ok) toast.success(`${ok} divergenze accettate e rimesse in automatico — ogni operazione è nel log modifiche`);
    load();
  };

  return (
    <div data-testid="divergenze-page" className="space-y-5">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Divergenze sincronizzazione</h1>
      <p className="text-xs text-slate-500 max-w-3xl">
        Qui compaiono i campi corretti a mano il cui valore non coincide con l'ultimo valore ricevuto dalla fonte esterna.
        Il valore manuale resta quello ufficiale finché non decidi diversamente.
      </p>
      {isMaster && <SimulaSync stores={stores} onDone={load} />}
      {divs && divs.length > 0 && (
        <div className="flex items-center gap-3">
          <button data-testid="div-bulk-automatica" onClick={bulkAutomatico} disabled={!selezionate.length || bulkBusy}
            className="bg-emerald-600 text-white text-sm font-semibold rounded-md px-4 py-1.5 disabled:opacity-40 hover:bg-emerald-700 transition-colors">
            {bulkBusy ? "Risoluzione in corso…" : `Accetta esterno e torna automatico (${selezionate.length})`}
          </button>
          <span data-testid="div-sel-count" className="text-xs text-slate-500">
            {selezionate.length} selezionate su {divs.length} — ogni operazione finisce nel log modifiche
          </span>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table data-testid="divergenze-table" className="w-full text-sm">
          <thead>
            <tr className="bg-slate-100/80 text-left">
              <th className="px-3 py-2.5">
                <input type="checkbox" data-testid="div-check-all" checked={tutteSel}
                  onChange={(e) => setSel(e.target.checked ? Object.fromEntries((divs || []).map((d) => [d.id, true])) : {})}
                  className="accent-emerald-600 w-4 h-4 align-middle" title="Seleziona tutte" />
              </th>
              {["Tipo", "Store", "Riferimento", "Campo", "Valore manuale (ufficiale)", "Valore esterno", "Modificato da", "Il", "Azioni"].map((h) => (
                <th key={h} className="px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-600 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(divs || []).map((d) => (
              <tr key={d.id} data-testid={`divergenza-row-${d.id}`} className="border-t border-slate-100">
                <td className="px-3 py-2">
                  <input type="checkbox" data-testid={`div-check-${d.id}`} checked={!!sel[d.id]}
                    onChange={(e) => setSel((s) => ({ ...s, [d.id]: e.target.checked }))}
                    className="accent-emerald-600 w-4 h-4 align-middle" />
                </td><td className="px-3 py-2 text-xs uppercase text-slate-500">{d.tipo}</td>
                <td className="px-3 py-2 text-xs">{d.store_nome}</td>
                <td className="px-3 py-2 font-mono text-xs">{d.tipo === "ordine" ? d.id_ordine_esterno : `ADV ${d.adv_id?.slice(0, 8)}`}</td>
                <td className="px-3 py-2 font-medium">{SYNC_FIELDS_LABELS[d.campo] || d.campo}</td>
                <td className="px-3 py-2 font-mono text-xs text-emerald-700">{d.valore_manuale}</td>
                <td className="px-3 py-2 font-mono text-xs text-amber-700">{d.valore_esterno}</td>
                <td className="px-3 py-2 text-xs">{d.modificato_da || "—"}</td>
                <td className="px-3 py-2 font-mono text-xs">{d.modificato_il ? new Date(d.modificato_il).toLocaleString("it-IT") : "—"}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    {AZIONI.map((a) => (
                      <button key={a.id} data-testid={`divergenza-${a.id}`} onClick={() => risolvi(d, a.id)}
                        className={`text-xs font-semibold rounded px-2 py-1 whitespace-nowrap transition-colors ${a.cls}`}>
                        {a.label}
                      </button>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
            {divs && divs.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-400">Nessuna divergenza aperta.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
